import * as React from 'react';
import { TwitterPicker } from 'react-color';
import { useTranslation } from 'react-i18next';
import { connect, DispatchProp } from 'react-redux';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

import { AbstractCode } from 'src/types/insights';
import { useCodebookContext } from '../Catalog/CodebookProvider';
import CodeChip from '../Common/CodeChip';
import { TAG_COLORS } from '../utils/color';
import CodeSelect from './CodeSelect';

interface BaseProps {
  closeDialog: () => void;
  codeType: string;
  codes: AbstractCode[];
  onSuccess: (
    code: Pick<
      AbstractCode,
      'color' | 'description' | 'name' | 'parent_id' | 'code_type'
    >
  ) => void;
  open: boolean;
}

type Props = BaseProps & DispatchProp;

const CreateCodeDialog: React.FunctionComponent<Props> = <
  CodeType extends AbstractCode
>(
  props: Props
) => {
  const { closeDialog, codes, onSuccess, open, codeType } = props;
  const { parentCodeId } = useCodebookContext();
  const { t } = useTranslation();

  const [color, setColor] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [name, setName] = React.useState('');
  const [nameError, setNameError] = React.useState('');
  const [parentId, setParentId] = React.useState(-1);
  const parentCode: AbstractCode | undefined = codes.filter(
    (code) => code.id === parentCodeId
  )[0];
  const disabledCodes: CodeType['id'][] = [];
  codes.map((currentCode: AbstractCode) => {
    if (
      currentCode.parentage &&
      currentCode.parentage.length !== 1 // is a child code
    ) {
      disabledCodes.push(currentCode.id);
    }
  });
  const calculatedColor =
    color || (parentCode !== undefined ? parentCode.color : '#ffffff');
  const calculatedSuggestedColor =
    color || (parentCode !== undefined ? parentCode.color : '#333333');

  React.useEffect(() => {
    // reset when opening only
    // avoids making the reset visible during close
    if (open) {
      const randomColor =
        TAG_COLORS[Math.floor(Math.random() * TAG_COLORS.length)];
      setColor(randomColor);
      setDescription('');
      setName('');
      setNameError('');
      setParentId(-1);
    }
    if (parentCodeId && parentCode) {
      setParentId(parentCodeId);
      setColor(parentCode.color);
    }
  }, [open, parentCodeId, parentCode]);

  const handleCancel = () => {
    closeDialog();
  };

  const handleSubmit = () => {
    if (name.length === 0) {
      setNameError(t('insights.name_error'));
    } else {
      closeDialog();
      onSuccess({
        color: calculatedColor,
        description: description,
        name: name,
        parent_id: parentId,
        code_type: codeType,
      });
    }
  };

  const handleCodeSelectChange = (parentId: number) => {
    setParentId(parentId);
  };

  const handleColorChangeComplete = (color: any) => {
    setColor(color.hex);
  };

  const exampleCode: AbstractCode = {
    id: -1,
    name: name,
    color: calculatedColor,
    parentage: [-1],
    code_type: codeType,
  };
  const exampleSuggestedCode: AbstractCode = {
    id: -1,
    name: name,
    color: calculatedSuggestedColor,
    parentage: [-1],
    code_type: codeType,
  };
  let dialogTitle;

  switch (codeType) {
    case 'thematic':
      dialogTitle = t('insights.create_thematic_code');
      break;
    case 'structural':
      dialogTitle = t('insights.create_structural_code');
      break;
    case 'demographic':
      dialogTitle = t('insights.create_demographic');
      break;
    default:
      dialogTitle = t('insights.create_code');
      break;
  }

  return (
    <Dialog
      data-testid="CreateCodeDialog"
      disableRestoreFocus
      onClose={closeDialog}
      open={open}
      maxWidth="xl"
    >
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <Box display={'flex'} justifyContent="center" gap={'12px'}>
          <Box>
            <CodeChip code={exampleCode} />
          </Box>
          <Box>
            <CodeChip code={exampleSuggestedCode} suggested />
          </Box>
        </Box>
        <Box sx={{ mt: 4 }}>
          <TextField
            autoFocus
            error={nameError.length > 0}
            fullWidth
            helperText={nameError}
            inputProps={{ maxLength: 40 }}
            label={t('common.name')}
            onChange={(event) => {
              if (event.target.value === '') {
                setNameError(t('insights.name_error'));
              } else if (event.target.value === 'Exclude from Portal') {
                setNameError(t('insights.reserve_name_error'));
              } else if (event.target.value.includes('|')) {
                setNameError(t('insights.pipe_character_error'));
              } else {
                setNameError('');
              }
              setName(event.target.value);
            }}
            value={name}
          />
        </Box>
        <Box sx={{ mt: 4 }}>
          <TextField
            fullWidth
            label={t('common.description')}
            multiline
            onChange={(event) => {
              setDescription(event.target.value);
            }}
            value={description}
          />
        </Box>
        <Box sx={{ mt: 4 }} display="flex" justifyContent="center">
          <TwitterPicker
            color={color}
            colors={TAG_COLORS}
            onChangeComplete={handleColorChangeComplete}
            triangle="hide"
            width="360px"
            styles={{
              default: {
                body: {
                  display: 'flex',
                  flexWrap: 'wrap',
                  columnGap: '6px',
                },
                input: {
                  marginLeft: '-6px',
                },
              },
            }}
          />
        </Box>
        <Box sx={{ mt: 4 }}>
          <CodeSelect
            codes={codes}
            disabledCodes={disabledCodes}
            onChange={handleCodeSelectChange}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>{t('common.cancel')}</Button>
        <Button onClick={handleSubmit}>{t('common.confirm')}</Button>
      </DialogActions>
    </Dialog>
  );
};

CreateCodeDialog.displayName = 'CreateCodeDialog';

export default connect()(CreateCodeDialog);
